import React from 'react';
import VirtualScroll from '@progress/kendo-react-dropdowns/dist/npm/common/VirtualScroll';
import { Keys } from '@progress/kendo-react-common';
import PureComponent from '../../../dispatcher/modules/component/PureComponent';
import PuzzleMenu from './PuzzleMenu';
import { 
  get, 
  isEmpty, 
  isFunction, 
  isNumber, 
  isUndefined, 
  uniqueId
} from 'lodash';
import { 
  PUZZLE_REMOTE_LIST_ABSTRACT_PROPS, 
  PUZZLE_REMOTE_LIST_ABSTRACT_STATE 
} from './Types';

class PuzzleRemoteListAbstract<P = {}, S = {}> extends PureComponent<PUZZLE_REMOTE_LIST_ABSTRACT_PROPS & P, PUZZLE_REMOTE_LIST_ABSTRACT_STATE & S> {
  readonly vs = new VirtualScroll();
  protected focusIndex: number | null = null;
  protected scrollSyncing = false;

  state: PUZZLE_REMOTE_LIST_ABSTRACT_STATE = {
    skip: 0,
    total: 0,
    pageSize: 20,
    data: [],
    dataCache: [],
    vsEnabled: true
  }

  constructor(props: PUZZLE_REMOTE_LIST_ABSTRACT_PROPS) {
    super(props);
    this.moduleId = uniqueId('puzzle-remote-menu_');
    if (this.props.onPageChange) {
      this.vs.PageChange = this.props.onPageChange;
    }
  }

  moduleDidMount() {
    this.vs.container.addEventListener('keydown', this.onKeyDown, {
      capture: true
    });
  }

  componentDidUpdate(prevProps: PUZZLE_REMOTE_LIST_ABSTRACT_PROPS, prevState: PUZZLE_REMOTE_LIST_ABSTRACT_STATE) {
    super.componentDidUpdate(prevProps, prevState);
    const { virtual } = this.props;
    let prevTotal;
    let total;
    if (!isUndefined(virtual)) {
      prevTotal = prevProps.virtual ? prevProps.virtual.total : 0;
      total = virtual.total;
    } else {
      prevTotal = prevState.total;
      total = this.state.total;
    }

    this.assignVirtualListRef();

    if (total !== prevTotal) {
      this.vs.calcScrollElementHeight();
      this.vs.reset();
    }

    this.focusItemAfterScrolling();
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.vs.container?.removeEventListener('keydown', this.onKeyDown);
  }

  private assignVirtualListRef = () => {
    if (this.props?.data?.length || this.state.data) {
      if (!this.vs.list) {
        this.vs.list = this.vs.container.querySelector('ul');
      }
    } else {
      this.vs.list = null;
    }
  }

  private focusItemAfterScrolling = () => {
    let {
      data,
      virtual
    } = this.props;

    const skip = !isUndefined(virtual) ? virtual.skip : this.state.skip;

    if (!isUndefined(virtual)) {
      data = this.state.data;
    }
    if (
      this.scrollSyncing &&
      isNumber(this.focusIndex) &&
      !isEmpty(data) &&
      data[0]?._populated &&
      (data[0]?._index === skip)
    ) {
      (this.vs.list.children[this.focusIndex] as HTMLLIElement).focus();
      Object.assign(this, {
        focusIndex: null,
        scrollSyncing: false
      });
    }
  };

  private isHidden = (element: HTMLLIElement, isUp: boolean): boolean => {
    const { height: containerHeight, y: containerY } = this.vs.container.getBoundingClientRect();
    const { height: elementHeight, y: elementY } = element.getBoundingClientRect();
    return isUp
      ? (containerY > elementY)
      : (elementY + elementHeight) > (containerY + containerHeight);
  };

  private onKeyDown = (event: any) => {
    const { keyCode } = event;

    if ([Keys.up, Keys.down].includes(keyCode)) {
      const { target } = event;
      let element;
      let isUp = false;

      switch (keyCode) {
        case Keys.up:
          isUp = true;
          ({ previousElementSibling: element } = target);
          break;
        case Keys.down:
          ({ nextElementSibling: element } = target);
          break;
      }

      event.stopPropagation();
      event.preventDefault();

      const { container, itemHeight, pageSize } = this.vs;

      if (element) {
        if (this.isHidden(element, isUp)) {
          const { scrollTop } = container;
          container.scrollTo(0, scrollTop + (itemHeight * (isUp ? -1 : 1)));

          if ((scrollTop === 0) || (isUp && (scrollTop === itemHeight))) {
            element.focus();
          }
        } else {
          element.focus();
        }
      } else {
        Object.assign(this, {
          focusIndex: isUp ? (pageSize - 1) : 0,
          scrollSyncing: true
        });
        container.scrollTo(0, isUp ? container.scrollHeight : 0);
      }
    }
    if (Keys.enter === keyCode) {
      event.preventDefault();
    }
  }

  /**
   * Call external parseData function
   * @param data
   */
  parseData = (data: any) => {
    if (isFunction(this.props.parseData)) {
      data = this.props.parseData(data, this.props.dataItemKey);
    }
    return data;
  }

  /**
   * Set data is function from virtual scroll component
   * preferably it should not be changed
   * @param data
   */
  setData = (data: any) => {
    get(this, 'props.setData') && this.props.setData(data);
  };

  private renderEmptyList = () => (
    <div
      style={{ textAlign: 'center' }}
    >
      {this.translate('emptyText')}
    </div>
  );

  private renderScrollElement = () => (
    <div className="k-height-container">
      <div
        ref={element => this.vs.scrollElement = element}
        key="scrollElementKey"
      />
    </div>
  );

  render() {
    let {
      className,
      data,
      itemRender,
      menuProps = {},
      virtual
    } = this.props;

    const { skip, total, pageSize, vsEnabled } = this.state;

    if (!isUndefined(virtual)) {
      this.vs.enabled = vsEnabled;
      this.vs.skip = virtual.skip;
      this.vs.total = virtual.total;
      this.vs.pageSize = virtual.pageSize;
    } else {
      this.vs.enabled = vsEnabled;
      this.vs.skip = skip;
      this.vs.total = total;
      this.vs.pageSize = pageSize;
      data = this.state.data;
    }

    return (
      <div
        className={className}
        onScroll={this.vs.scrollHandler}
        style={{
          maxHeight: '245px',
          overflow: 'auto'
        }}
        ref={this.vs.scrollerRef}
      >
        {isEmpty(data)
          ? this.renderEmptyList()
          : (
            <PuzzleMenu
              {...menuProps}
            >
              {data.map(itemRender)}
            </PuzzleMenu>
          )
        }
        {vsEnabled && this.renderScrollElement()}
      </div>
    );
  }
}

export default PuzzleRemoteListAbstract;
